import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BreadCrumb from "../Controls/BreadCrumb";
import LocationListItem from "../ListItems/LocationListItem";
import PersonAgreementListItem from "../ListItems/PersonAgreementListItem";
import ContactInfoListItem from "../ListItems/ContactInfoListItem";
import CertificationListItem from "../ListItems/CertificationListItem";
import EducationListItem from "../ListItems/EducationListItem";
import EmployerPreferenceListItem from "../ListItems/EmployerPreferenceListItem";
import LocationEditModal from "../Modals/LocationEditModal";
import AgreementDetailsModal from "../Modals/AgreementDetailsModal";
import ContactInfoEditModal from "../Modals/ContactInfoEditModal";
import EducationEditModal from "../Modals/EducationEditModal";
import CertificationEditModal from "../Modals/CertificationEditModal";
import EmployerPrefEditModal from "../Modals/EmployerPrefEditModal";
import UserData from "../Controls/UserData";
import Help from "../Controls/Help";
import { FaUser } from "react-icons/fa";
import { MdAddBox } from "react-icons/md";
import { APPEARANCE_MODES } from "../../theme/appearance";
import { persistAppearanceMode } from "../../theme/persistAppearance";
import { useTheme } from "../../theme/ThemeProvider";
import { useAccountProfileQuery, useRefreshAccountProfile } from "../../query/useAccountProfile";
import API from "@src/API";
import { toast } from "react-toastify";

export default function AccountView() {
  const refreshAccount = useRefreshAccountProfile();
  const { data, isLoading, isError, error } = useAccountProfileQuery();
  const [user] = useState(UserData.User());
  const [locationModalId, setLocationModalId] = useState(null);
  const [agreementModal, setAgreementModal] = useState(null);
  const [contactInfoModalId, setContactInfoModalId] = useState(null);
  const [educationModalId, setEducationModalId] = useState(null);
  const [certificationModalId, setCertificationModalId] = useState(null);
  const [employerPrefModalId, setEmployerPrefModalId] = useState(null);
  const [savingAppearance, setSavingAppearance] = useState(false);
  const [savingNoticeChannel, setSavingNoticeChannel] = useState(false);
  const { appearanceMode } = useTheme();
  const navigate = useNavigate();

  const locations = data?.locations || [];
  const agreements = data?.agreements || [];
  const contactInfos = data?.contactInfos || [];
  const education = data?.education || [];
  const certifications = data?.certifications || [];
  const employerPreferences = data?.employerPreferences || [];
  const noticeChannel =
    data?.person?.VerificationExpiryNoticeChannel ||
    data?.person?.verificationExpiryNoticeChannel ||
    "Email";

  const personEdit = () => {
    navigate("/personEdit/" + user.userId);
  };

  async function onAppearanceChange(mode) {
    setSavingAppearance(true);
    try {
      await persistAppearanceMode(mode);
      await refreshAccount();
    } finally {
      setSavingAppearance(false);
    }
  }

  async function onNoticeChannelChange(channel) {
    setSavingNoticeChannel(true);
    try {
      const resp = await API.postAsync("/Person/VerificationExpiryNoticeChannel", { channel });
      if (resp?.ok) {
        toast.success("Reminder preference saved");
        await refreshAccount();
      } else {
        toast.error(resp?.jsonData?.message || "Could not save preference");
      }
    } finally {
      setSavingNoticeChannel(false);
    }
  }

  async function closeLocationModal() {
    setLocationModalId(null);
    await refreshAccount();
  }
  const closeAgreementModal = () => setAgreementModal(null);
  async function closeContactInfoModal() {
    setContactInfoModalId(null);
    await refreshAccount();
  }
  async function closeEducationModal() {
    setEducationModalId(null);
    await refreshAccount();
  }
  async function closeCertificationModal() {
    setCertificationModalId(null);
    await refreshAccount();
  }
  async function closeEmployerPrefModal() {
    setEmployerPrefModalId(null);
    await refreshAccount();
  }

  return (
    <div className="pagebody">
      <BreadCrumb props={["Account"]} />
      <div className="tabbody">
        <h2>My Account</h2>
        <span className="listTitleIcons">
          <FaUser onClick={personEdit} />
        </span>

        {isLoading ? <p className="fieldHint">Loading account…</p> : null}
        {isError ? (
          <p className="fieldHint">{error?.message || "Could not load account."}</p>
        ) : null}

        <div className="tabsection">
          <div className="listTitle">
            <span>Appearance</span>
          </div>
          <fieldset className="appearanceMode" disabled={savingAppearance}>
            <legend className="sr-only">Appearance mode</legend>
            {APPEARANCE_MODES.map((mode) => (
              <label key={mode} className="appearanceModeOption">
                <input
                  type="radio"
                  name="appearanceMode"
                  value={mode}
                  checked={appearanceMode === mode}
                  onChange={() => onAppearanceChange(mode)}
                />
                <span>{mode.charAt(0).toUpperCase() + mode.slice(1)}</span>
              </label>
            ))}
          </fieldset>
          <p className="appearanceModeHint">
            System follows your browser or device preference.
          </p>
        </div>

        <div className="tabsection" data-testid="verification-expiry-notice-channel">
          <div className="listTitle">
            <span>Certification expiry reminders</span>
          </div>
          <fieldset
            className="appearanceMode"
            disabled={savingNoticeChannel}
          >
            <legend className="sr-only">Expiry reminder channel</legend>
            {["Email", "Sms", "Both", "Off"].map((channel) => (
              <label key={channel} className="appearanceModeOption">
                <input
                  type="radio"
                  name="verificationExpiryNoticeChannel"
                  value={channel}
                  checked={noticeChannel === channel}
                  onChange={() => onNoticeChannelChange(channel)}
                />
                <span>
                  {channel === "Sms" ? "Text" : channel}
                </span>
              </label>
            ))}
          </fieldset>
          <p className="appearanceModeHint">
            We remind you when a certification enters the 30-day renew window (at most every 30
            days). Choose email, text, both, or off.
          </p>
        </div>

        <div className="tabsection">
          <div className="listTitle">
            <span>
              My Locations
              <Help id="2128" />
            </span>
            <div className="listTitleIcons">
              <MdAddBox
                data-testid="location-add"
                role="button"
                aria-label="Add location"
                onClick={() => setLocationModalId(0)}
              />
            </div>
          </div>
          <ul>
            {locations.map((l) => (
              <LocationListItem
                key={l.ID}
                location={l}
                onEdit={(id) => setLocationModalId(id)}
              />
            ))}
          </ul>
        </div>
        {locationModalId !== null ? (
          <LocationEditModal locationId={locationModalId} onClose={closeLocationModal} />
        ) : null}
        <div className="tabsection">
          <div className="listTitle">
            <span>
              Agreements
              <Help id="2129" />
            </span>
            <div className="listTitleIcons"></div>
          </div>
          <ul>
            {agreements.map((a) => (
              <PersonAgreementListItem
                key={a.ID ?? a.AgreementID}
                agreement={a}
                onView={(agr) => setAgreementModal(agr)}
              />
            ))}
          </ul>
        </div>
        {agreementModal ? (
          <AgreementDetailsModal agreement={agreementModal} onClose={closeAgreementModal} />
        ) : null}
        <div className="tabsection">
          <div className="listTitle">
            <span>
              Contact Info
              <Help id="2130" />
            </span>
            <div className="listTitleIcons">
              <MdAddBox
                data-testid="contact-info-add"
                role="button"
                aria-label="Add contact info"
                onClick={() => setContactInfoModalId(0)}
              />
            </div>
          </div>
          <ul>
            {contactInfos.map((ci) => (
              <ContactInfoListItem
                key={ci.ID}
                contactInfo={ci}
                onEdit={(id) => setContactInfoModalId(id)}
              />
            ))}
          </ul>
        </div>
        {contactInfoModalId !== null ? (
          <ContactInfoEditModal
            contactInfoId={contactInfoModalId}
            onClose={closeContactInfoModal}
          />
        ) : null}

        <div className="tabsection">
          <div className="listTitle">
            <span>
              Education ({education.length})
              <Help id="2131" />
            </span>
            <div className="listTitleIcons">
              <MdAddBox
                data-testid="education-add"
                role="button"
                aria-label="Add education"
                onClick={() => setEducationModalId(0)}
              />
            </div>
          </div>
          <ul>
            {education.map((edu) => (
              <li key={edu.ID || edu.id}>
                <EducationListItem
                  education={edu}
                  onEdit={(id) => setEducationModalId(id)}
                />
              </li>
            ))}
          </ul>
        </div>
        {educationModalId !== null ? (
          <EducationEditModal
            educationId={educationModalId}
            onClose={closeEducationModal}
          />
        ) : null}

        <div className="tabsection">
          <div className="listTitle">
            <span>
              Certifications ({certifications.length})
              <Help id="2131" />
            </span>
            <div className="listTitleIcons">
              <MdAddBox
                data-testid="certification-add"
                role="button"
                aria-label="Add certification"
                onClick={() => setCertificationModalId(0)}
              />
            </div>
          </div>
          <ol>
            {certifications.map((cert) => (
              <CertificationListItem
                key={cert.ID}
                certification={cert}
                onEdit={(id) => setCertificationModalId(id)}
              />
            ))}
          </ol>
        </div>
        {certificationModalId !== null ? (
          <CertificationEditModal
            certificationId={certificationModalId}
            onClose={closeCertificationModal}
          />
        ) : null}
        <div className="tabsection">
          <div className="listTitle">
            <span>Feedback &amp; bugs</span>
          </div>
          <p>
            <Link to="/feedback" data-testid="feedback-link">
              Send feedback or report a bug
            </Link>
          </p>
        </div>
        <div className="tabsection">
          <div className="listTitle">
            <span>
              Employer Preferences
              <Help id="2132" />
            </span>
            <div className="listTitleIcons">
              <MdAddBox
                data-testid="employer-pref-add"
                role="button"
                aria-label="Add employer preference"
                onClick={() => setEmployerPrefModalId(0)}
              />
            </div>
          </div>
          <ol>
            {employerPreferences.map((pref) => (
              <EmployerPreferenceListItem
                key={pref.ID}
                preferences={pref}
                onEdit={(id) => setEmployerPrefModalId(id)}
              />
            ))}
          </ol>
        </div>
        {employerPrefModalId !== null ? (
          <EmployerPrefEditModal
            preferenceId={employerPrefModalId}
            onClose={closeEmployerPrefModal}
          />
        ) : null}
      </div>
    </div>
  );
}
